import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, Fan as Fax } from 'lucide-react';
import { analytics } from '../utils/analytics';
import { sendFormSubmission, fallbackToMailto } from '../utils/emailService';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    department: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Track contact form submission
      analytics.trackContactForm(formData.department || 'general');

      // Try to send via email service first
      const result = await sendFormSubmission({
        formType: 'contact',
        formData
      });

      if (result.success) {
        setIsSubmitted(true);
        setSubmitMessage(result.message);
        
        // Reset form after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            department: '',
            message: ''
          });
        }, 5000);
      } else {
        // If email service fails, fall back to mailto
        setSubmitMessage('Redirecting to your email client...');
        setTimeout(() => {
          fallbackToMailto(formData.department || 'contact', formData);
          setIsSubmitted(true);
          setSubmitMessage('Your email client should now be open with your message. Please send the email to complete your inquiry.');
        }, 1000);
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      // Fall back to mailto on any error
      setSubmitMessage('Redirecting to your email client...');
      setTimeout(() => {
        fallbackToMailto(formData.department || 'contact', formData);
        setIsSubmitted(true);
        setSubmitMessage('Your email client should now be open with your message. Please send the email to complete your inquiry.');
      }, 1000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneClick = () => {
    analytics.trackPhoneCall();
  };

  const handleEmailClick = () => {
    analytics.trackEmailClick();
  };

  const departments = [
    { value: "Sales", label: "Sales", email: "sales@outstandingnet.com" },
    { value: "Billing", label: "Billing", email: "billing@outstandingnet.com" },
    { value: "Support", label: "Support", email: "support@outstandingnet.com" },
    { value: "General Inquiry", label: "General Inquiry", email: "support@outstandingnet.com" }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-32 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Get In Touch
            </h1>
            <p className="text-xl sm:text-2xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Ready to start your project? Let's discuss your vision and create something outstanding together.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Contact Form */}
            <div className="card p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>
              
              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-accent-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600 mb-4">
                    {submitMessage}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        placeholder="Your full name"
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        placeholder="your@email.com"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        placeholder="Your company name"
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        placeholder="Your phone number"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                      Department *
                    </label>
                    <select
                      id="department"
                      name="department"
                      required
                      value={formData.department}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                      disabled={isSubmitting}
                    >
                      <option value="">Select a department</option>
                      {departments.map((department) => (
                        <option key={department.value} value={department.value}>
                          {department.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message Details *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                      placeholder="Please provide details about what you're contacting us regarding..."
                      disabled={isSubmitting}
                    ></textarea>
                  </div>

                  {submitMessage && !isSubmitted && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-700">{submitMessage}</p>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full btn-primary text-lg py-4 flex items-center justify-center ${
                      isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Sending Message...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Information - Matching Height */}
            <div className="card p-8 flex flex-col justify-between min-h-full">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Contact Information
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  We're here to help bring your vision to life. Reach out to us through any of the following methods.
                </p>

                {/* Contact Details Grid */}
                <div className="space-y-6 mb-8">
                  {/* Phone & Email Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      <div>
                        <a 
                          href="tel:5862484115"
                          onClick={handlePhoneClick}
                          className="font-medium text-gray-900 hover:text-primary-600 transition-colors"
                        >
                          (586) 248-4115
                        </a>
                        <p className="text-sm text-gray-600">Mon-Fri 9am-5pm</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      <div>
                        <a 
                          href="mailto:support@outstandingnet.com"
                          onClick={handleEmailClick}
                          className="font-medium text-gray-900 hover:text-primary-600 transition-colors"
                        >
                          support@outstandingnet.com
                        </a>
                        <p className="text-sm text-gray-600">24/7 support</p>
                      </div>
                    </div>
                  </div>

                  {/* Fax & Hours Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <Fax className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-900">(248) 238-8869</p>
                        <p className="text-sm text-gray-600">Secure documents</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-900">Mon-Fri 9am-5pm</p>
                        <p className="text-sm text-gray-600">Weekend by appointment</p>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-primary-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium text-gray-900">11908 Cedarwood Dr</p>
                      <p className="text-gray-600">Shelby Township, MI 48315</p>
                      <p className="text-sm text-gray-600">Office visits by appointment</p>
                    </div>
                  </div>
                </div>

                {/* Department Directory */}
                <div className="border-t border-gray-200 pt-6 mb-8">
                  <h3 className="font-semibold text-gray-900 mb-4">Department Directory</h3>
                  <div className="space-y-3">
                    {departments.map((dept, index) => (
                      <div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
                        <span className="text-sm font-medium text-gray-700">{dept.label}</span>
                        <a 
                          href={`mailto:${dept.email}`}
                          onClick={handleEmailClick}
                          className="text-primary-600 hover:text-primary-700 font-medium text-sm break-all transition-colors"
                        >
                          {dept.email}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Actions - Positioned at bottom */}
              <div className="flex flex-col sm:flex-row gap-3 mt-auto">
                <a 
                  href="tel:5862484115" 
                  onClick={handlePhoneClick}
                  className="flex-1 bg-primary-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors text-center"
                >
                  Call Now
                </a>
                <a 
                  href="mailto:support@outstandingnet.com" 
                  onClick={handleEmailClick}
                  className="flex-1 border border-primary-600 text-primary-600 px-4 py-3 rounded-lg font-medium hover:bg-primary-50 transition-colors text-center"
                >
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card overflow-hidden">
            <div className="h-96 bg-gray-200 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Our Location</h3>
                <p className="text-gray-600">11908 Cedarwood Dr</p>
                <p className="text-gray-600">Shelby Township, MI 48315</p>
                <p className="text-sm text-gray-500 mt-2">Interactive map will be integrated here</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about working with us and our process.
            </p>
          </div>

          <div className="space-y-8">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                How quickly can you start my project?
              </h3>
              <p className="text-gray-600">
                We typically begin new projects within 1-2 weeks of contract signing, depending on our current workload. 
                For urgent projects, we can often accommodate faster start times.
              </p>
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Do you offer payment plans?
              </h3>
              <p className="text-gray-600">
                Yes, we offer flexible payment plans for larger projects. Typically, we require 50% upfront and 50% upon completion, 
                but we can work with you to create a payment schedule that fits your budget.
              </p>
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Will I own my website after it's built?
              </h3>
              <p className="text-gray-600">
                Absolutely! Once the project is completed and paid for, you own all rights to your website, including 
                source code, designs, and content. We also provide all necessary login credentials and documentation.
              </p>
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What if I need changes after the website is live?
              </h3>
              <p className="text-gray-600">
                We provide ongoing support and maintenance services. Minor content updates are often included in our 
                maintenance packages, while larger changes can be handled on an hourly basis or as separate projects.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Transform Your Online Presence?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Don't wait any longer. Contact us today for a free consultation and let's discuss how we can help your business grow online.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:5862484115" 
              onClick={handlePhoneClick}
              className="btn-secondary text-lg px-8 py-4"
            >
              Call Now: (586) 248-4115
            </a>
            <a 
              href="mailto:support@outstandingnet.com" 
              onClick={handleEmailClick}
              className="btn-outline text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-primary-600"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};